import React, { useState } from 'react';
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  useToast,
  VStack,
  HStack,
  Text,
  Image,
  Card,
  CardBody,
  Divider,
  Icon,
  Alert,
  AlertIcon,
  Link,
  Container,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiMail, FiLock, FiHeart, FiUsers, FiFileText } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import supabase from '../supabaseClient';

const ParentSignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);

  const navigate = useNavigate();
  const toast = useToast();
  const { login } = useAuth();

  // Color mode values
  const bgGradient = useColorModeValue(
    'linear(to-br, blue.50, purple.50, pink.50)',
    'linear(to-br, gray.900, blue.900, purple.900)'
  );
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.300');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setShowResend(false);

    try {
      const { user, error } = await login(email, password);

      if (error) {
        if (error.toLowerCase().includes('email not confirmed')) {
          setShowResend(true);
        }
        throw new Error(error);
      }

      // Check if user is a parent
      if (user.role !== 'parent') {
        throw new Error('This portal is only for parents. Please use the staff portal for therapist and admin access.');
      }

      toast({
        title: 'Welcome back!',
        description: 'Successfully signed in to your parent portal.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate('/parent-dashboard');
    } catch (error) {
      toast({
        title: 'Sign In Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });

      if (error) throw error;

      toast({
        title: 'Verification Email Sent',
        description: 'Please check your inbox and verify your email.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setShowResend(false);
    } catch (error) {
      toast({
        title: 'Failed to Send Email',
        description: error.message || 'An error occurred.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box minH="100vh" bg={bgGradient}>
      <Container maxW="7xl" py={8}>
        <Flex direction={{ base: 'column', lg: 'row' }} align="center" minH="90vh">
          {/* Left Side - Branding and Features */}
          <Box flex={1} pr={{ lg: 12 }} mb={{ base: 8, lg: 0 }}>
            <VStack align="start" spacing={8}>
              {/* Logo and Title */}
              <VStack align="start" spacing={4}>
                <HStack spacing={3}>
                  <Box
                    w={12}
                    h={12}
                    bg="linear-gradient(135deg, #24b5e8, #abdb15)"
                    borderRadius="xl"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={FiHeart} color="white" boxSize={6} />
                  </Box>
                  <VStack align="start" spacing={0}>
                    <Heading size="lg" color="#24b5e8">
                      Tiny Triumphs
                    </Heading>
                    <Text fontSize="md" color={textColor} fontWeight="medium">
                      Therapy Center
                    </Text>
                  </VStack>
                </HStack>
                
                <Box>
                  <Heading size="xl" mb={4} color="gray.800">
                    Parent Portal
                  </Heading>
                  <Text fontSize="lg" color={textColor} lineHeight="tall">
                    Stay connected with your child's therapeutic journey. Access session notes, 
                    track progress, and communicate with our therapy team.
                  </Text>
                </Box>
              </VStack>

              {/* Features */}
              <VStack align="start" spacing={4} w="full">
                <Heading size="md" color="gray.700">
                  What you can do:
                </Heading>
                
                <HStack spacing={4}>
                  <Icon as={FiFileText} color="#24b5e8" boxSize={5} />
                  <Text color={textColor}>View all therapy documents and session notes</Text>
                </HStack>
                
                <HStack spacing={4}>
                  <Icon as={FiUsers} color="#abdb15" boxSize={5} />
                  <Text color={textColor}>Track your child's therapy sessions and progress</Text>
                </HStack>
                
                <HStack spacing={4}>
                  <Icon as={FiHeart} color="#dc4291" boxSize={5} />
                  <Text color={textColor}>Request documents and communicate with therapists</Text>
                </HStack>
              </VStack>

              {/* Contact Info */}
              <Box pt={4}>
                <Text fontSize="sm" color={textColor}>
                  <strong>3rd Floor Allegro Center</strong><br />
                  Pasong Tamo Ext., Makati<br />
                  📞 +639171600036<br />
                  ✉️ tinytriumphsph@gmail.com
                </Text>
              </Box>
            </VStack>
          </Box>

          {/* Right Side - Sign In Form */}
          <Box flex={1} w="full" maxW="md">
            <Card 
              bg={cardBg}
              shadow="2xl"
              borderRadius="2xl"
              border="1px solid"
              borderColor={useColorModeValue('gray.200', 'gray.700')}
            >
              <CardBody p={8}>
                <VStack spacing={6} align="stretch">
                  <Box textAlign="center">
                    <Heading size="lg" mb={2} color="gray.800">
                      Welcome Back
                    </Heading>
                    <Text color={textColor}>
                      Sign in to access your child's therapy portal
                    </Text>
                  </Box>

                  <Divider />

                  <form onSubmit={handleSubmit}>
                    <VStack spacing={5} align="stretch">
                      <FormControl isRequired>
                        <FormLabel color="gray.700" fontWeight="medium">
                          Email Address
                        </FormLabel>
                        <HStack>
                          <Icon as={FiMail} color="gray.400" />
                          <Input
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            isDisabled={isLoading}
                            size="lg"
                            focusBorderColor="#24b5e8"
                            borderRadius="lg"
                          />
                        </HStack>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Use the same email registered with the center
                        </Text>
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel color="gray.700" fontWeight="medium">
                          Password
                        </FormLabel>
                        <HStack>
                          <Icon as={FiLock} color="gray.400" />
                          <Input
                            type="password"
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            isDisabled={isLoading}
                            size="lg"
                            focusBorderColor="#24b5e8"
                            borderRadius="lg"
                          />
                        </HStack>
                      </FormControl>

                      <Button
                        type="submit"
                        size="lg"
                        bg="linear-gradient(135deg, #24b5e8, #abdb15)"
                        color="white"
                        borderRadius="lg"
                        isLoading={isLoading}
                        loadingText="Signing In..."
                        _hover={{
                          bg: "linear-gradient(135deg, #1a8bc4, #8bc212)",
                          transform: "translateY(-1px)",
                          shadow: "lg",
                        }}
                        transition="all 0.2s"
                        w="full"
                      >
                        Sign In to Portal
                      </Button>

                      {showResend && (
                        <Alert status="warning" borderRadius="lg">
                          <AlertIcon />
                          <VStack align="start" spacing={2} flex={1}>
                            <Text fontSize="sm">
                              Please verify your email address first.
                            </Text>
                            <Button
                              variant="link"
                              size="sm"
                              color="#24b5e8"
                              onClick={handleResendVerification}
                              isDisabled={!email || isLoading}
                            >
                              Resend verification email
                            </Button>
                          </VStack>
                        </Alert>
                      )}
                    </VStack>
                  </form>

                  <Divider />

                  <Box textAlign="center">
                    <Text fontSize="sm" color={textColor}>
                      New to our center?{' '}
                      <Link color="#24b5e8" fontWeight="medium">
                        Contact us to create your account
                      </Link>
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      Need help? Call us at +639171600036
                    </Text>
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

export default ParentSignIn;